/**
 * Game Configuration Schema
 */
export interface GameConfig {
  id: string;
  title: string;
  description: string;
  category: 'retro' | 'modern' | 'puzzle' | 'action' | 'casual';
  thumbnail?: string;
  tier: 'free' | 'premium';
  multiplayer: boolean;
  controls: {
    keyboard: string[];
    mouse?: boolean;
    touch?: boolean;
  };
  difficulty: 'easy' | 'medium' | 'hard';
  estimatedPlayTime: string; // e.g., "5-10 min"
}

export interface RegisteredGame {
  config: GameConfig;
  component: React.ComponentType<GameComponentProps>;
}

export interface GameComponentProps {
  onScoreChange?: (score: number) => void;
  onGameOver?: (finalScore: number) => void;
  onExit?: () => void;
}

/**
 * Registry of all available games
 */
class GameRegistryClass {
  private games: Map<string, RegisteredGame> = new Map();

  /**
   * Register a game
   */
  register(game: RegisteredGame): void {
    if (this.games.has(game.config.id)) {
      console.warn(`Game ${game.config.id} is already registered, overwriting`);
    }
    this.games.set(game.config.id, game);
  }

  /**
   * Get a game by ID
   */
  get(gameId: string): RegisteredGame | undefined {
    return this.games.get(gameId);
  }

  /**
   * Get all registered games
   */
  getAll(): RegisteredGame[] {
    return Array.from(this.games.values());
  }

  /**
   * Get games filtered by category
   */
  getByCategory(category: GameConfig['category']): RegisteredGame[] {
    return this.getAll().filter((g) => g.config.category === category);
  }

  /**
   * Get games filtered by tier
   */
  getByTier(tier: GameConfig['tier']): RegisteredGame[] {
    return this.getAll().filter((g) => g.config.tier === tier);
  }

  /**
   * Get free games
   */
  getFreeGames(): RegisteredGame[] {
    return this.getByTier('free');
  }

  /**
   * Get premium games
   */
  getPremiumGames(): RegisteredGame[] {
    return this.getByTier('premium');
  }

  /**
   * Check if a game exists
   */
  has(gameId: string): boolean {
    return this.games.has(gameId);
  }

  /**
   * Get count of registered games
   */
  get count(): number {
    return this.games.size;
  }
}

// Singleton instance
export const GameRegistry = new GameRegistryClass();

// Categories metadata
export const GAME_CATEGORIES: Record<
  GameConfig['category'],
  { label: string; icon: string; description: string }
> = {
  retro: {
    label: 'Retro Corner',
    icon: '🕹️',
    description: 'Classic arcade games from the golden age',
  },
  modern: {
    label: 'Modern Classics',
    icon: '🎮',
    description: 'Updated takes on timeless gameplay',
  },
  puzzle: {
    label: 'Brain Teasers',
    icon: '🧩',
    description: 'Test your problem-solving skills',
  },
  action: {
    label: 'Action Zone',
    icon: '⚡',
    description: 'Fast-paced excitement',
  },
  casual: {
    label: 'Casual Fun',
    icon: '🌟',
    description: 'Quick games for a quick break',
  },
};
